/**
 * TipModal - Support an artist with a tip (Stripe checkout).
 * Reusable across Live Room, profiles, etc.
 */

import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Heart, Loader2 } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const TIP_PRESETS = [5, 10, 20, 50];

const TipModal = ({ open, onClose, artistId, artistName }) => {
  const { token } = useAuth();
  const [amount, setAmount] = useState(10);
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const submit = async () => {
    if (!token) { setError('Please sign in to tip.'); return; }
    if (!artistId) { setError('Artist not available for tips.'); return; }
    if (Number(amount) < 1) { setError('Minimum tip is $1.'); return; }
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`${API_URL}/api/store/tip`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ origin_url: window.location.origin, artist_id: artistId, amount: Number(amount), message })
      });
      const data = await res.json();
      if (res.ok && data.checkout_url) {
        window.location.href = data.checkout_url;
      } else {
        setError(data.detail || 'Could not start tip checkout.');
        setLoading(false);
      }
    } catch (e) {
      setError('Network error. Please try again.');
      setLoading(false);
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/80 backdrop-blur-md z-50" onClick={onClose} data-testid="tip-modal-backdrop" />
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.9 }}
              className="w-full max-w-md bg-[#12101A] rounded-3xl border border-white/10 overflow-hidden"
              onClick={(e) => e.stopPropagation()} data-testid="tip-modal">
              <div className="p-5 border-b border-white/10 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Heart className="w-5 h-5 text-[#FF007F]" />
                  <h3 className="text-white font-bold text-lg">Support {artistName || 'Artist'}</h3>
                </div>
                <button onClick={onClose} className="text-[#71717A] hover:text-white" data-testid="tip-modal-close">
                  <X className="w-5 h-5" />
                </button>
              </div>
              <div className="p-5 space-y-4">
                {error && <div className="p-3 rounded-xl bg-red-500/15 border border-red-500/30 text-red-300 text-sm">{error}</div>}
                <div className="grid grid-cols-4 gap-2">
                  {TIP_PRESETS.map((amt) => (
                    <button key={amt} onClick={() => setAmount(amt)}
                      className={`py-2 rounded-xl font-semibold text-sm transition-colors ${
                        Number(amount) === amt ? 'bg-[#FF007F] text-white' : 'bg-white/5 text-[#A1A1AA] hover:bg-white/10'
                      }`} data-testid={`tip-preset-${amt}`}>
                      ${amt}
                    </button>
                  ))}
                </div>
                <div className="flex items-center gap-2 bg-white/5 rounded-xl px-3">
                  <span className="text-[#A1A1AA]">$</span>
                  <input type="number" min="1" max="500" value={amount} onChange={(e) => setAmount(e.target.value)}
                    className="flex-1 bg-transparent py-3 text-white outline-none" data-testid="tip-custom-amount" />
                </div>
                <input type="text" placeholder="Add a message (optional)" value={message} maxLength={140}
                  onChange={(e) => setMessage(e.target.value)}
                  className="w-full bg-white/5 rounded-xl px-3 py-3 text-white outline-none placeholder-[#52525B] text-sm" data-testid="tip-message" />
                <button onClick={submit} disabled={loading || Number(amount) < 1}
                  className="w-full py-4 rounded-2xl bg-gradient-to-r from-[#FF007F] to-[#FF007F]/80 text-white font-bold text-lg hover:opacity-90 transition-opacity disabled:opacity-50 flex items-center justify-center gap-2"
                  data-testid="tip-confirm-btn">
                  {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <><Heart className="w-5 h-5" /> Tip ${Number(amount || 0).toFixed(2)}</>}
                </button>
                <p className="text-center text-[#52525B] text-xs">Secure checkout powered by Stripe</p>
              </div>
            </motion.div>
          </div>
        </>
      )}
    </AnimatePresence>
  );
};

export default TipModal;
